import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { ButtonRendererComponent } from 'src/app/renderer/button-renderer.component';
import { DashboardService } from 'src/app/_services/dashboard.service';
import { LanguageTranslateService } from 'src/app/_services/language-translate.service';
import { UtilServiceService } from 'src/app/_services/utilService/util-service.service';
import { ErrorUtil } from 'src/app/_utilities/error';
import { SubscriptionUtil } from 'src/app/_utilities/subscription';
import { TableHeaderToolTipComponent } from 'src/app/shared/table-header-tool-tip/table-header-tool-tip.component';
import { AuthenticationService } from 'src/app/auth/authentication.service';
import moment from 'moment-timezone';

@Component({
  selector: 'app-asset-open-job',
  templateUrl: './asset-open-job.component.html',
  styleUrl: './asset-open-job.component.scss',
  standalone: false,
})
export class AssetOpenJobComponent
  extends SubscriptionUtil
  implements OnInit, OnDestroy
{
  public filterActive = true;
  title: any;
  data: any;

  totalRecord: any;
  searchValue: any;
  searchValueData = false;
  translate: any;

  gridApi: any;
  gridOptions: any;
  columnDefs: any;
  defaultColDef: any;
  overlayNoRowsTemplate: any;
  frameworkComponents: any;
  pageSize: any = 25;
  gridParams: any;
  startFrom: any;
  parameters: any;
  records: any[] = [];

  equipmentStatusOptions: any[] = [];
  equipmentTypeOptions: any[] = [];
  groupOptions: any[] = [];
  serviceZoneOptions: any[] = [];

  filteredOptions = {
    assetsStatusId: null as string | number | null,
    assetsTypeId: null as string | number | null,
    userGroupId: null as string | number | null,
    serviceZoneId: null as string | number | null,
  };

  constructor(
    public modalRef: BsModalRef,
    private dashboardService: DashboardService,
    private errorUtil: ErrorUtil,
    private languageTranslateService: LanguageTranslateService,
    private router: Router,
    private util: UtilServiceService,
    private auth: AuthenticationService
  ) {
    super();
    this.preInit();
  }

  ngOnInit(): void {
    this.init();
  }

  ngOnDestroy(): void {
    this.onDestroy();
  }

  /******************************************** Pre Init ******************************************/
  private preInit() {
    this.frameworkComponents = {
      buttonRenderer: ButtonRendererComponent,
    };

    this.gridOptions = {
      cacheBlockSize: 25,
      paginationPageSize: 25,
      rowModelType: 'infinite',
      sortingOrder: ['desc', 'asc'],
      onGridSizeChanged: (params: any) => {
        params.api.sizeColumnsToFit();
      },
      tooltipShowDelay: 100,
    };

    this.defaultColDef = {
      sortable: true,
      resizable: true,
      filter: false,
      enableRowGroup: true,
      enablePivot: true,
      enableValue: true,
      tooltipComponent: TableHeaderToolTipComponent,
    };

    this.overlayNoRowsTemplate =
      '<span style="padding: 10px; font-size: 14px;">Record not found</span>';
  }

  /******************************************** Init ******************************************/
  private init() {
    this.initFilterData();
    this.title = this.title || 'Open' + ' '+ this.translate.jobOrder.label.plural + ' ' + 'by ' + this.translate?.jobOrder.fields.status.singular;
    this.getLanguageData();
  }

  private initFilterData(): void {
    const filters = this.data?.filters || {};
    this.equipmentStatusOptions = this.data?.equipmentStatusOptions || [];
    this.equipmentTypeOptions = this.data?.equipmentTypeOptions || [];
    this.groupOptions = this.data?.groupOptions || [];
    this.serviceZoneOptions = this.data?.serviceZoneOptions || [];

    this.filteredOptions = {
      assetsStatusId:
        filters.assetsStatusId ?? filters.selectedEquipmentStatus ?? null,
      assetsTypeId:
        filters.assetsTypeId ?? filters.selectedEquipmentType ?? null,
      userGroupId: filters.userGroupId ?? filters.selectedGroup ?? null,
      serviceZoneId:
        filters.serviceZoneId ?? filters.selectedServiceZone ?? null,
    };
  }

  /******************************************** On Destroy ******************************************/
  private onDestroy() {
    super.ngOnDestroy();
  }

  /******************************************** Language ******************************************/
  getLanguageData() {
    this.push(
      this.languageTranslateService.wordSrc$.subscribe((data) => {
        if (Object.keys(data).length !== 0) {
          this.translate = data;
          this.initializeColumns();
        }
      })
    );
  }

  push(obs: any) {
    super.push(obs);
  }

  /******************************************** On Grid Ready ******************************************/
  public onGridReady(params: any) {
    this.gridParams = params;
    this.searchValue = this.searchValue ? this.searchValue : '';
    this.gridApi = params.api;

    const dataSource = {
      getRows: (params: any) => {
        this.startFrom = params.startRow + 1;

        if (params.sortModel.length <= 0) {
          this.parameters = { colId: 'jobOrderNumber', sort: 'desc' };
          params.sortModel.push(this.parameters);
        }

        const apiParams = this.buildApiParams(params);

        this.push(
          this.dashboardService.getAssetOpenJobOrders(apiParams).subscribe(
            (res: any) => {
              this.gridApi.hideOverlay();

              const list = this.extractList(res);
              this.records = list.map((item: any) => this.mapRow(item));
              const total = this.extractTotal(res, list);
              const body = res?.body ?? res;
              const jobOrders = body?.jobOrders ?? body;
              const status =
                jobOrders?.status ??
                body?.status ??
                res?.status ??
                (list.length ? 'HAS_DATA' : 'NO_DATA');

              if (
                status === 'NO_DATA' ||
                status === 'SEARCH_NO_RESULTS' ||
                this.records.length === 0
              ) {
                this.gridApi.showNoRowsOverlay();
                this.totalRecord = 0;
                params.successCallback([], 0);
              } else {
                this.totalRecord = total;
                params.successCallback(this.records, total);
              }
            },
            (error) => {
              let errorTitle = error?.error?.title;
              if (error?.error?.type == 'dynamic-lang-error') {
                const data =
                  error.error.instance +
                  '.validationBE.' +
                  error.error.detail;
                errorTitle = this.languageTranslateService.getNestedValue(
                  this.translate,
                  data
                );
              }
              this.errorUtil.setErrorMessage(
                error.status,
                null,
                errorTitle,
                'error',
                2000
              );
              this.totalRecord = 0;
              params.successCallback([], 0);
              this.gridApi.showNoRowsOverlay();
            }
          )
        );
      },
    };

    this.gridApi.sizeColumnsToFit();
    this.gridApi.setGridOption('datasource', dataSource);
  }

  /******************************************** Build API Params ******************************************/
  private buildApiParams(gridParams: any): any {
    const page = Math.floor(gridParams.startRow / this.pageSize) + 1;

    return {
      page,
      perPage: this.pageSize,
      columnName: gridParams.sortModel[0]?.colId || 'jobOrderNumber',
      sortOrder: gridParams.sortModel[0]?.sort || 'desc',
      assetsStatusId: this.resolveFilterId(this.filteredOptions.assetsStatusId),
      assetsTypeId: this.resolveFilterId(this.filteredOptions.assetsTypeId),
      userGroupId: this.resolveFilterId(this.filteredOptions.userGroupId),
      serviceZoneId: this.resolveFilterId(this.filteredOptions.serviceZoneId),
      search: this.searchValue || null,
    };
  }

  private resolveFilterId(value: any): string | number | null {
    if (value == null || value === '') {
      return null;
    }
    if (typeof value === 'object') {
      return value.id ?? value.value ?? null;
    }
    return value;
  }

  private extractList(res: any): any[] {
    const body = res?.body ?? res;
    const jobOrders = body?.jobOrders ?? body;

    if (Array.isArray(jobOrders?.data)) {
      return jobOrders.data;
    }
    if (Array.isArray(jobOrders)) {
      return jobOrders;
    }
    return jobOrders?.content ?? body?.data ?? body?.content ?? [];
  }

  private extractTotal(res: any, list: any[]): number {
    const body = res?.body ?? res;
    const jobOrders = body?.jobOrders ?? body;

    return (
      Number(body?.totalOpenCount) ||
      Number(jobOrders?.total) ||
      Number(jobOrders?.recordsTotal) ||
      Number(jobOrders?.recordsFiltered) ||
      Number(body?.total) ||
      list.length
    );
  }

  private mapRow(item: any): any {
    const jobStatusName = String(
      item?.jobStatus?.name ||
      item?.jobStatus ||
      item?.status?.name ||
      item?.status ||
      item?.jobStatusName ||
      item?.statusName ||
      ''
    ).trim();

    const matchedStatus = this.getMatchedJobOrderStatus(jobStatusName);

    return {
      ...item,
      jobOrderId: item?.id || item?.jobOrderId || item?.jobOrder?.id,
      jobOrder: item?.jobOrderNumber || item?.jobOrderNo || item?.number || '-',
      jobOrderName: item?.jobOrderName || item?.name || '-',
      equipmentName: item?.equipmentName || item?.assetName || '-',
      equipmentSerial: item?.equipmentSerialNo || item?.assetSerialNo || '',
      jobStatus: matchedStatus?.fieldLabel || matchedStatus?.label || jobStatusName || '-',
      jobStatusClass: matchedStatus?.colorCode || '',
      jobType:
        item?.jobType?.name ||
        item?.jobOrderType?.name ||
        item?.jobTypeName ||
        item?.jobType ||
        '-',
      orderDate: this.formatOrderDate(item?.orderDate || item?.installationTime || item?.jobOrder?.installationTime),
      assignee:
        item?.assignee?.fullName ||
        item?.assignee ||
        item?.assignedTo ||
        '-',
    };
  }

  private formatOrderDate(orderDate: any): string {
    if (orderDate == null || orderDate === '') {
      return '-';
    }

    const dateValue =
      typeof orderDate === 'object'
        ? orderDate.date || orderDate.dateTime || orderDate.value || orderDate
        : orderDate;

    const timezone = this.auth.getUserTimezone();
    const dateFormat = localStorage.getItem('date_format')?.toUpperCase() || 'DD/MM/YYYY';
    const parsed = moment.tz(dateValue, timezone);

    if (!parsed.isValid()) {
      return '-';
    }

    return `${parsed.format(dateFormat)}`;
  }

  private getMatchedJobOrderStatus(jobStatusName: string): any {
    const options = this.data?.jobOrderStatusOptions || [];
    if (!jobStatusName || !options?.length) {
      return null;
    }

    return (
      options.find(
        (status: any) =>
          String(status?.name || '').toLowerCase() === jobStatusName.toLowerCase()
      ) || null
    );
  }

  /******************************************** Search ******************************************/
  public onSearch() {
    if (this.searchValue && this.searchValue.length > 0) {
      this.searchValueData = true;
      this.refresh();
      return;
    }

   
  }
public onSearchCheck() {
    if (this.searchValue?.length == 0) {
      this.searchValueData = false;
      this.searchValue = '';
      this.refresh();
    }
  }
  public onClear() {
    this.searchValueData = false;
    this.searchValue = '';
    this.refresh();
  }

  /******************************************** Acts as Refresh ******************************************/
  public refresh() {
    this.onGridReady(this.gridParams);
  }

  /******************************************** View ******************************************/
  public view(e: any) {
    if (
      (e.colDef.field === 'jobOrder' ||
        e.colDef.colId === 'jobOrderNumber' ||
        e.colDef.field === 'jobOrderName') &&
      e.data?.jobOrderId
    ) {
      this.openJob(e.data.jobOrderId);
    }
  }

  private openJob(jobOrderId: string | number) {
    const url = this.router.serializeUrl(
      this.router.createUrlTree(['/dashboard/jobOrders/view/' + jobOrderId], {
        queryParams: { jobOrderId },
      })
    );
    this.util.openItemToNewTab(url);
  }

  /******************************************** Context Menu ******************************************/
  public getContextMenuItems = (params: any) => {
    params.node.data.field = params.column.colId;
    const results: any[] = [];

    if (
      (params.column.colId === 'jobOrder' ||
        params.column.colId === 'jobOrderNumber' ||
        params.column.colId === 'jobOrderName') &&
      params.node.data?.jobOrderId
    ) {
      results.push({
        name: 'Open link in new tab',
        action: () => this.openJob(params.node.data.jobOrderId),
      });
    }

    return results;
  };

  /******************************************** toggle filter ******************************************/
  public toggleFilter() {
    this.filterActive = !this.filterActive;
  }

  onFilterChange(
    key: 'assetsStatusId' | 'assetsTypeId' | 'userGroupId' | 'serviceZoneId',
    value: any
  ): void {
    this.filteredOptions[key] = value;
    this.refresh();
  }

  clearAll(): void {
    this.filteredOptions = {
      assetsStatusId: null,
      assetsTypeId: null,
      userGroupId: null,
      serviceZoneId: null,
    };
    this.refresh();
  }

  /******************************************** Columns ******************************************/
  private initializeColumns() {
    this.overlayNoRowsTemplate =
      '<span style="padding: 10px; font-size: 14px;">' +
      (this.translate?.general?.messages?.noRecordsFound || 'Record not found') +
      '</span>';

    this.columnDefs = [
      {
        headerName: this.translate.jobOrder.label.singular,
        field: 'jobOrder',
        colId: 'jobOrderNumber',
        width: 140,
        headerTooltip: '#',
        cellClass: function () {
          return ['text-1-5 font-weight-bold table_default_color'];
        },
      },
      {
        headerName: this.translate.asset.label.singular,
        field: 'equipmentName',
        width: 180,
        headerTooltip: '#',
        cellRenderer: function (params: any) {
          if (!params?.data) {
            return '';
          }
          const name = params.data.equipmentName || '-';
          const serial = params.data.equipmentSerial;
          if (serial) {
            return (
              '<div class="d-flex flex-column">' +
              '<span>' + name + '</span>' +
              '<span class="text-muted" style="font-size: 11px;">' + serial + '</span>' +
              '</div>'
            );
          }
          return name;
        },
      },
      {
        headerName: 'Job Status',
        field: 'jobStatus',
        width: 150,
        headerTooltip: '#',
        cellRenderer: function (params: any) {
          if (!params?.data) {
            return '';
          }
          const status = params.data.jobStatus || '-';
          const color = params.data.jobStatusClass;
          if (color) {
            return (
              '<span class="p-mr-2 p-tag p-component" style="background-color:' +
              color +
              '50 !important ;color:' +
              color +
              '"><span class="p-tag-value text-dark">' +
              status +
              '</span></span>'
            );
          }
          return status;
        },
      },
      {
        headerName: 'Job Type',
        field: 'jobType',
        width: 150,
        headerTooltip: '#',
      },
      {
        headerName: this.translate?.jobOrder?.fields?.orderDate?.singular || 'Order Date',
        field: 'orderDate',
        colId: 'orderDate',
        width: 170,
        headerTooltip: '#',
      },
      {
        headerName: 'Assignee',
        field: 'assignee',
        width: 160,
        headerTooltip: '#',
      },
    ];

    if (this.gridApi) {
      this.gridApi.setGridOption('columnDefs', this.columnDefs);
      setTimeout(() => {
        this.gridApi.sizeColumnsToFit();
      }, 300);
    }
  }
}
